/**
 * CleverTap Helper Utilities
 * Provides helper functions for tracking CleverTap events
 */

import { getDeviceId } from '../api/commonApi';
import { CT_PARAMS } from '../analytics/trackerEvents/cleverTap/parameters';
import { getFbclid, getGclid, getTitleId, getStoredMobile, storeUserMobile } from './utmTracker';

// Storage key for user mobile number
const MOBILE_STORAGE_KEY = 'ct_user_mobile';
// Storage key for user IP address
const USER_IP_STORAGE_KEY = 'ct_user_ip';

/**
 * Get GAID (Google Advertising ID) for web
 * For web, we use the device ID stored in localStorage
 */
export const getGAID = (): string => {
  return getDeviceId();
};

/**
 * Store user mobile number in sessionStorage after successful login
 * This mobile number will be automatically appended to all subsequent CT events
 * @param mobile - The user's mobile number (without country code prefix)
 */
export const setUserMobile = (mobile: string): void => {
  if (mobile) {
    // Store just the mobile number (e.g., "9876543210")
    sessionStorage.setItem(MOBILE_STORAGE_KEY, mobile);
  }
};

/**
 * Get stored user mobile number from sessionStorage
 * @returns The stored mobile number or null if not set
 */
export const getUserMobile = (): string | null => {
  return sessionStorage.getItem(MOBILE_STORAGE_KEY);
};

/**
 * Clear stored user mobile number (e.g., on logout)
 */
export const clearUserMobile = (): void => {
  sessionStorage.removeItem(MOBILE_STORAGE_KEY);
};

/**
 * Check if CleverTap is loaded and available
 */
export const isCleverTapLoaded = (): boolean => {
  return typeof window !== 'undefined' && !!(window as any).clevertap;
};

/**
 * Get user IP address using open source API (ipify.org)
 * Caches the IP in sessionStorage to avoid multiple API calls
 * @returns Promise that resolves to the IP address string or null if failed
 */
export const getUserIP = async (): Promise<string | null> => {
  // Check if IP is already cached in sessionStorage
  const cachedIP = sessionStorage.getItem(USER_IP_STORAGE_KEY);
  if (cachedIP) {
    return cachedIP;
  }

  try {
    // Use ipify.org API to get user IP
    const response = await fetch('https://api.ipify.org?format=json', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch IP: ${response.status}`);
    }

    const data = await response.json();
    const ip = data.ip;

    if (ip) {
      // Cache the IP in sessionStorage for the session
      sessionStorage.setItem(USER_IP_STORAGE_KEY, ip);
      return ip;
    }

    return null;
  } catch (error) {
    console.warn('[CleverTap] Failed to fetch user IP:', error);
    return null;
  }
};

/**
 * Get cached user IP from sessionStorage
 * @returns The cached IP address or null if not available
 */
export const getCachedUserIP = (): string | null => {
  return sessionStorage.getItem(USER_IP_STORAGE_KEY);
};

// ============================================
// WEB_EVENT_* Events that require titleId
// ============================================
const EVENTS_REQUIRING_TITLE_ID = [
  'WEB_EVENT_TRial_SCREEN_VIEW',
  'WEB_EVENT_SCREEN_VIEW_SIGN_UP',
  'WEB_EVENT_PHONE_PROCEED',
  'WEB_EVENT_SCREEN_VIEW_OTP',
  'WEB_EVENT_POPUP_VIEW_AUTO_READ',
  'WEB_EVENT_POPUP_AUTO_READ_RESPONDE',
  'WEB_EVENT_OTP_INPUT',
  'WEB_EVENT_LOGIN',
  'WEB_EVENT_PAYMENT_SCREEN_VIEW',
  'WEB_EVENT_PAYMENT_STATUS_SCREEN_VIEW',
  'WEB_EVENT_DOWNLOAD_APP_CLICKED',
  'WEB_EVENT_RETRY_PAYMENT_CLICKED',
  'WEB_EVENT_STATUS_SCREEN_VIEW',
];

// ============================================
// Post-Login Events (require titleId + mobile)
// ============================================
const POST_LOGIN_EVENTS = [
  'WEB_EVENT_PAYMENT_SCREEN_VIEW',
  'WEB_EVENT_PAYMENT_STATUS_SCREEN_VIEW',
  'WEB_EVENT_DOWNLOAD_APP_CLICKED',
  'WEB_EVENT_RETRY_PAYMENT_CLICKED',
  'WEB_EVENT_STATUS_SCREEN_VIEW',
];

// Flag to track if IP fetch is in progress
let ipFetchInProgress = false;

/**
 * Initialize user IP fetch in the background
 * This is called once when the app loads to cache the IP early
 */
export const initializeUserIP = (): void => {
  // Only fetch if not cached and not already in progress
  if (!getCachedUserIP() && !ipFetchInProgress) {
    ipFetchInProgress = true;
    getUserIP()
      .then(() => {
        ipFetchInProgress = false;
      })
      .catch(() => {
        ipFetchInProgress = false;
      });
  }
};

/**
 * Track a CleverTap event
 * Automatically adds:
 * - GAID and FBCLID for all WEB_EVENT_* events
 * - User IP for all WEB_EVENT_* events (uses cached IP if available)
 * - titleId for specified events
 * - mobile for post-login events (after WEB_EVENT_LOGIN)
 * 
 * @param eventName - Name of the event to track
 * @param eventData - Optional event data/properties
 * @param retryCount - Number of retries if CleverTap is not loaded (default: 3)
 * @param retryDelay - Delay between retries in ms (default: 500)
 */
export const trackCleverTapEvent = (
  eventName: string,
  eventData?: Record<string, any>,
  retryCount: number = 3,
  retryDelay: number = 500
): void => {
  // Get cached user IP (if not cached, it will be fetched in background for next time)
  const userIP = getCachedUserIP();
  
  // If IP is not cached, trigger background fetch for future events
  if (!userIP && !ipFetchInProgress) {
    initializeUserIP();
  }

  // Automatically add params for all WEB_EVENT_* events
  let finalEventData = eventData;
  if (eventName.startsWith('WEB_EVENT_')) {
    const fbclid = getFbclid();
    const gclid = getGclid();
    const titleId = getTitleId();
    const storedMobile = getStoredMobile();
    
    // Base params for all WEB_EVENT_* events
    finalEventData = {
      ...(eventData || {}),
      [CT_PARAMS.GAID]: getGAID(),
      ...(fbclid ? { [CT_PARAMS.FBCLID]: fbclid } : {}),
      ...(gclid ? { [CT_PARAMS.GCLID]: gclid } : {}),
      ...(userIP ? { [CT_PARAMS.USER_IP]: userIP } : {})
    };
    
    // Add titleId for events that require it
    if (EVENTS_REQUIRING_TITLE_ID.includes(eventName) && titleId) {
      finalEventData[CT_PARAMS.TITLE_ID] = titleId;
    }
    
    // Add mobile for post-login events
    if (POST_LOGIN_EVENTS.includes(eventName) && storedMobile) {
      finalEventData[CT_PARAMS.MOBILE_NUMBER] = storedMobile;
    }
    
    // Special handling for WEB_EVENT_LOGIN: 
    // Store mobile number for future events (mobile_number is already in eventData)
    if (eventName === 'WEB_EVENT_LOGIN') {
      const mobileFromEvent = eventData?.[CT_PARAMS.MOBILE_NUMBER] || eventData?.mobile_number;
      if (mobileFromEvent) {
        storeUserMobile(mobileFromEvent);
      }
    }
  } else {
    // For non-WEB_EVENT_* events, add user IP if available
    if (userIP) {
      finalEventData = {
        ...(eventData || {}),
        [CT_PARAMS.USER_IP]: userIP
      };
    }
  }

  if (isCleverTapLoaded()) {
    try {
      if (finalEventData && Object.keys(finalEventData).length > 0) {
        (window as any).clevertap.event.push(eventName, finalEventData);
      } else {
        (window as any).clevertap.event.push(eventName);
      }
      console.log('[CleverTap] Event tracked:', eventName, finalEventData || '');
    } catch (error) {
      console.error('[CleverTap] Error tracking event:', error);
    }
  } else if (retryCount > 0) {
    // Retry if CleverTap is not loaded yet
    setTimeout(() => {
      trackCleverTapEvent(eventName, eventData, retryCount - 1, retryDelay);
    }, retryDelay);
  } else {
    console.warn('[CleverTap] CleverTap not available, event not tracked:', eventName);
  }
};

/**
 * Wait for CleverTap to be loaded, then execute a callback
 * @param callback - Function to execute when CleverTap is loaded
 * @param timeout - Maximum time to wait in ms (default: 5000)
 */
export const waitForCleverTap = (
  callback: () => void,
  timeout: number = 5000
): void => {
  if (isCleverTapLoaded()) {
    callback();
    return;
  }

  const startTime = Date.now();
  const checkInterval = setInterval(() => {
    if (isCleverTapLoaded()) {
      clearInterval(checkInterval);
      callback();
    } else if (Date.now() - startTime > timeout) {
      clearInterval(checkInterval);
      console.warn('[CleverTap] Timeout waiting for CleverTap to load');
    }
  }, 100);
};

